// Tiny client-side Vimeo thumbnail resolver. Uses Vimeo's public oEmbed
// endpoint (no auth required) and caches results in localStorage so we
// don't hit the network on every render.

const STORAGE_KEY = "jsir_vimeo_thumbs_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type Cache = Record<string, { url: string; ts: number }>;

function readCache(): Cache {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"); } catch { return {}; }
}
function writeCache(c: Cache) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(c)); } catch {}
}

export function vimeoIdFromUrl(url: string): string | null {
  const m = url.match(/(?:vimeo\.com|player\.vimeo\.com\/video)\/(\d+)/);
  return m ? m[1] : null;
}

export function vimeoWatchUrl(url: string): string {
  const id = vimeoIdFromUrl(url);
  return id ? `https://vimeo.com/${id}` : url;
}

const inflight: Record<string, Promise<string | null>> = {};

export function fetchVimeoThumbnail(url: string): Promise<string | null> {
  const id = vimeoIdFromUrl(url);
  if (!id) return Promise.resolve(null);

  const cache = readCache();
  const hit = cache[id];
  if (hit && Date.now() - hit.ts < TTL_MS) return Promise.resolve(hit.url);

  const existing = inflight[id];
  if (existing) return existing;

  const oembed = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}&width=640`;
  inflight[id] = fetch(oembed)
    .then((r) => (r.ok ? r.json() : null))
    .then((j: any) => {
      const thumb: string | undefined = j?.thumbnail_url;
      if (thumb) {
        const c = readCache();
        c[id] = { url: thumb, ts: Date.now() };
        writeCache(c);
        return thumb;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => { delete inflight[id]; });
  return inflight[id];
}
